import { Component, inject, OnDestroy } from '@angular/core';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { KeysService } from './keys.service';
import { DropdownDirective } from '../../ui/dropdown/dropdown.directive';
import { SvgIconComponent } from '../../ui/svg-icon/svg-icon.component';
import { SvgMarketIconComponent } from '../../ui/svg-icon/svg-market-icon.component';
import { FeatureName, TradeKeyWithPermissions } from '../../core/model/common';
import { YandexMetricService } from '../../core/service/yandex-metric.service';
import { RequireAuthService } from '../../core/service/require-auth.service';
import { MarketHelper } from '../../core/service/market.helper';
import { KeysHelper } from './keys.helper';
import { TradeKey } from '../../core/model/api';
import dayjs from 'dayjs';
import { BalanceFlowService } from '../balance/balance-flow.service';
import { RoundCurrencyPrettyComponent } from "../../ui/round/round-currency-pretty/round-currency-pretty.component";
import { concatMap, from, map, mergeAll, tap } from 'rxjs';
import { AuthenticatorService } from 'src/app/core/service/authenticator.service';
import { environment } from 'src/environments/environment';
import { SeparatorComponent } from "../../ui/separator/separator.component";
import { AppTranslateService } from 'src/app/core/service/translate.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-keys',
  imports: [AsyncPipe, DropdownDirective, NgTemplateOutlet, NgClass, SvgIconComponent, SvgMarketIconComponent, RoundCurrencyPrettyComponent, SeparatorComponent, TranslateModule],
  templateUrl: './keys.component.html',
  styleUrl: './keys.component.css'
})
export class KeysComponent implements OnDestroy {

  private keysService = inject(KeysService)
  private balanceFlow = inject(BalanceFlowService)
  private ym = inject(YandexMetricService)
  private requireAuth = inject(RequireAuthService)
  private authenticator = inject(AuthenticatorService)
  private marketHelper = inject(MarketHelper)
  private translate = inject(AppTranslateService)

  protected keysHelper = inject(KeysHelper)
  protected keys$ = this.keysService.keys$
  protected activeKey$ = this.keysService.activeKey$
  protected auth$ = this.authenticator.auth$
  protected balances = new Map<number, number>()
  protected diaryUrl = environment.diary.url

  private sb = this.keysService.keys$.pipe(
    concatMap(keys => from(keys)),
    map(key => this.balanceFlow.getBalanceFlow(key).pipe(
      tap(balance => this.balances.set(key.id, balance))
    )),
    mergeAll()
  ).subscribe()

  ngOnDestroy() {
    this.sb.unsubscribe()
  }

  protected selectKey(key: TradeKeyWithPermissions) {
    this.requireAuth.registerFeatureUsage(FeatureName.changeActiveKey).subscribe(() => {
      this.keysService.triggerActiveKey(key)
      this.ym.reachChangeKey()
    })
  }

  protected keyMarketName(key: TradeKey) {
    return this.marketHelper.resolveFullName(key.market, this.marketHelper.resolveDefaultBoard(key.market))
  }

  protected generateKeyHint(key: TradeKeyWithPermissions) {
    const mn = this.keyMarketName(key)
    let str = this.translate.get('keys.key.hint', { name: mn.name, board: mn.boardShort, keyName: this.keysHelper.resolveKeyName(key)});

    if (key.isDemo) {
      str += this.translate.get('keys.demo.hint')
    } else if (!key.canRead) {
      str += this.translate.get('keys.read.hint')
    } else if (key.canTrade) {
      str += this.translate.get('keys.trade.hint')
    } else {
      str += this.translate.get('keys.read.hint')
    }

    if (key.created) {
      str += `\n${this.translate.get('keys.created.hint', { date: dayjs(key.created * 1000).format('DD.MM.YYYY') })}`
    }

    if (!key.isDemo && key.canRead && key.ips != undefined) {
      if (key.ips.length) {
        str += `\n${this.translate.get('keys.ips.hint')}`
      } else {
        str += `\n${this.translate.get('keys.ips.hint')}`
      }
    }
    return str.trimEnd()
  }

}
